import * as React from 'react'
import { BaseInputProps, InputText } from '@paytrace/pt-skel/Component'
import { toInteger } from 'lodash'

interface Props extends BaseInputProps {
  strip?: string
  pattern?: string
  removeCustomInputWrap?: boolean
  fieldErrorMessage?: string
  showInlineError?: boolean
  onValidationError?: (message: any, isVisible: boolean) => void
  disableHTML5Validation?: boolean
  runValidationNow: boolean
}

type ValidationOpt = {
  minLength?: number
  maxLength?: number
  pattern?: string
  required?: boolean
}

const validateField = (value: string, { minLength, maxLength, pattern, required = false }: ValidationOpt): string => {
  const regexPattern = new RegExp(pattern || '.*')

  if (required && !value.trim()) {
    return 'Required field cannot be left empty.'
  }
  if (minLength !== undefined && value.length < toInteger(minLength)) {
    return `Minimum ${minLength} characters are required but (${value.length} characters are entered).`
  }
  if (maxLength !== undefined && value.length > toInteger(maxLength)) {
    return `Maximum ${maxLength} characters are allowed but (${value.length} characters are entered).`
  }
  if (!regexPattern.test(value)) {
    return 'Value does not match the required pattern.'
  }
  return ''
}

const stripValue = (pattern: string, value: string): string => {
  const rxStrip = new RegExp(pattern, 'g')
  return value.replace(rxStrip, '')
}

const trimValue = (value: string, maxLength: number): string => {
  const isMaxLengthExceeded = maxLength > 0 && value.length > maxLength
  return isMaxLengthExceeded ? value.slice(0, maxLength) : value
}

export function InlineValidationInputText (props: Props) {
  const { strip, onChange, style, value, className, ...validationProps } = props
  const {
    required = false,
    maxLength = 50, minLength,
    min, max, pattern,
    fieldErrorMessage,
    showInlineError = true,
    disableHTML5Validation = true,
    runValidationNow,
    onValidationError,
    ...rest
  } = validationProps
  const { onBlur } = rest

  const [internalErrorMessage, setInternalErrorMessage] = React.useState('')
  const showInputError = Boolean(internalErrorMessage) && showInlineError

  // tracks if the user has touched the input field
  const hasInputChangedRef = React.useRef(false)

  // store the latest callback reference in a ref to prevent render loops
  const onValidationErrorRef = React.useRef(onValidationError)
  React.useEffect(() => {
    onValidationErrorRef.current = onValidationError
  }, [onValidationError])

  const evaluateValidation = React.useCallback((val: string) => {
    const computedErrorMessage = validateField(val, { minLength, maxLength, pattern, required })
    const isInvalid = !!computedErrorMessage
    const currentActiveError = fieldErrorMessage || computedErrorMessage

    // show visual error only if the user has changed the field or after form submission
    const shouldShowVisualError = isInvalid && (hasInputChangedRef.current || runValidationNow)
    setInternalErrorMessage(shouldShowVisualError ? currentActiveError : '')

    // Always notify parent form of validation status
    if (onValidationErrorRef.current) {
      onValidationErrorRef.current(isInvalid ? currentActiveError : null, shouldShowVisualError)
    }
    return isInvalid
  }, [minLength, maxLength, pattern, required, fieldErrorMessage, runValidationNow])

  // re-evaluate validation whenever the external formatted `value` changes (e.g., after formatting amount on blur)
  React.useEffect(() => {
    if (value !== undefined && value !== null) {
      evaluateValidation(String(value))
    }
  }, [value, evaluateValidation])

  // Handle value changes during typing
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasInputChangedRef.current = true
    const rawValue = e.target.value
    const sanitizedValue = strip ? stripValue(strip, rawValue) : rawValue
    evaluateValidation(sanitizedValue)
    e.target.value = trimValue(sanitizedValue, maxLength)
    if (onChange) onChange(e)
  }

  // re-evaluation value onBlur (when focus leaves the input)
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    evaluateValidation(e.target.value)
    if (onBlur) onBlur(e)
  }

  const defaultErrorDiv = (
    <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>
      {internalErrorMessage}
    </div>
  )

  const html5ValidationProps = (disableHTML5Validation ? {} : { min, max, minLength, maxLength, pattern, required })
  const classNames = `${className || ''} ${showInputError ? 'input-error-state' : ''}`.trim()
  return (
    <InputText
      {...rest}
      {...html5ValidationProps}
      value={value}
      style={style}
      className={classNames}
      onChange={handleChange}
      onBlur={handleBlur}
      inlineErrorDiv={showInlineError && showInputError ? defaultErrorDiv : undefined}
    />
  )
}
